// src/services/apiService.ts
/* eslint-disable no-prototype-builtins */
import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';

// --- Load API Base URL from config.json (allows runtime configuration without rebuild) ---
const FALLBACK_URL = "https://backend.orchit.ai/api/v1/";
// const FALLBACK_URL = "https://backend.streamed.space/api/v1/";

// Synchronous config loader using XMLHttpRequest (works before async support)
function loadConfigSync(): string {
  try {
    const xhr = new XMLHttpRequest();
    // Add cache buster to prevent caching
    xhr.open('GET', '/config.json?_t=' + new Date().getTime(), false); // false = synchronous
    xhr.setRequestHeader('Cache-Control', 'no-cache, no-store, max-age=0');
    xhr.send();


    if (xhr.status === 200) {
      const config = JSON.parse(xhr.responseText);
      if (config.API_BASE_URL) {
        console.log('✅ Successfully loaded API Base URL from config.json:', config.API_BASE_URL);
        return config.API_BASE_URL;
      } else {
        console.warn('⚠️ config.json found but API_BASE_URL not set, using fallback');
      }
    } else {
      console.warn('⚠️ Failed to load config.json, status:', xhr.status);
    }
  } catch (error: any) {
    console.error('❌ Error loading config.json:', error.message);
  }
  console.log('🔧 Using fallback URL:', FALLBACK_URL);
  return FALLBACK_URL;
}

// Load config synchronously before creating the API client
const envBaseUrl = loadConfigSync();
console.log('🔧 Final Environment Base URL:', envBaseUrl);
// --- End Configuration Check ---

class APICaller {
  // 1. Explicitly declare class properties (Fixes TS2339)
  baseURL: string | undefined;
  apiClient: AxiosInstance;

  constructor() {
    // Use the determined base URL
    this.baseURL = envBaseUrl;
    console.log('🔧 Constructor - baseURL set to:', this.baseURL);

    // Fallback if env variable is not set (though it's hardcoded now)
    if (!this.baseURL) {
      this.baseURL = '/api'; // Assuming proxy or same-origin API
      console.warn('API base URL not found. Using default:', this.baseURL);
    }

    // Create an Axios instance with default configuration
    this.apiClient = axios.create({
      baseURL: this.baseURL,
      timeout: 10000, // Optional: Set a timeout
      headers: {
        'Content-Type': 'application/json',
        // Add other default headers if needed
      },
    });
    console.log('🔧 Axios instance created with baseURL:', this.baseURL);

    // Request interceptor to add the Bearer token
    this.apiClient.interceptors.request.use(
      // 2. Explicitly type parameters (Fixes TS7006)
      (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
        const token = this.getToken();
        if (token && config.headers) { // Check if headers exist before assigning
          config.headers['Authorization'] = `Bearer ${token}`;
        } else if (!token && !config.url?.includes('cms/login')) {
          // Only log missing token for non-login requests
          console.log('API Request Token: No token found for', config.url);
        }
        return config;
      },
      (error: AxiosError): Promise<AxiosError> => { // Explicitly type 'error' (Fixes TS7006)
        return Promise.reject(error);
      }
    );

    // Response interceptor for global error handling (optional)
    this.apiClient.interceptors.response.use(
      // 3. Explicitly type parameters (Fixes TS7006)
      (response: AxiosResponse): AxiosResponse => { // Explicitly type 'response' (Fixes TS7006)
        // Any status code that lies within the range of 2xx causes this function to trigger
        return response;
      },
      (error: AxiosError): Promise<AxiosError> => { // Explicitly type 'error' (Fixes TS7006)
        // Any status codes that fall outside the range of 2xx cause this function to trigger
        if (error.response?.status === 401) {
          // Handle unauthorized access, e.g., redirect to login
          console.warn('Unauthorized access - token might be invalid or expired.');

          // Avoid infinite redirect loop if already on login page
          // if (!window.location.pathname.includes('/login')) {
          //   console.warn('Unauthorized access - triggering professional logout'); 
          //   localStorage.removeItem('token');
          //   window.location.href = '/login?expired=true';
          // }
        }
        return Promise.reject(error); // Re-throw the error so individual calls can handle it
      }
    );
  }

  // --- Token Management Methods ---
  getToken(): string | null {
    return localStorage.getItem('token'); // Updated to match auth store
  }

  setToken(token: string): void { // Explicitly type 'token' (Fixes TS7006)
    localStorage.setItem('token', token); // Updated to match auth store
  }

  clearToken(): void {
    localStorage.removeItem('token'); // Updated to match auth store
  }
  // -------------------------------

  /**
   * Standardizes the API response based on your backend's ResponseHelper.
   * Assumes successful responses are wrapped like { status: 'success', message: '...', data: <actual_data> }.
   * @param response The raw Axios response object.
   * @returns The unwrapped data.
   */
  private standardizeResponse(response: AxiosResponse): any {
    // Check if response has the expected wrapper structure from ResponseHelper.success
    if (response.data && response.data.status === true && response.data.hasOwnProperty('data')) {
      // Handle the specific package categories response structure
      if (response.data.data && response.data.data.categories && response.data.data.pagination) {
        return {
          data: response.data.data,
          total: response.data.data.pagination.totalItems || 0,
          page: response.data.data.pagination.currentPage || 1,
          limit: response.data.data.pagination.limit || 10,
          pagination: response.data.data.pagination
        };
      }

      // For paginated responses with pagination at root level
      if (response.data.pagination) {
        return {
          data: response.data.data,
          total: response.data.pagination.total || response.data.pagination.totalItems || 0,
          page: response.data.pagination.page || response.data.pagination.currentPage || 1,
          limit: response.data.pagination.limit || 10,
          pagination: response.data.pagination
        };
      }

      // For non-paginated responses, return just the data
      return response.data.data;
    }

    // Handle features response structure
    if (response.data && response.data.data && response.data.data.features && response.data.data.pagination) {
      return {
        data: {
          features: response.data.data.features,
          pagination: response.data.data.pagination
        },
        total: response.data.data.pagination.totalItems || response.data.data.features.length,
        page: response.data.data.pagination.currentPage || 1,
        limit: response.data.data.pagination.limit || 10,
        pagination: response.data.data.pagination
      };
    }

    // Handle case where the response data is already unwrapped (packages directly in response.data)
    if (response.data && response.data.packages && response.data.pagination) {
      return {
        packages: response.data.packages,
        total: response.data.pagination.totalItems || response.data.packages.length,
        page: response.data.pagination.currentPage || 1,
        limit: response.data.pagination.limit || 10,
        pagination: response.data.pagination
      };
    }

    // Fallback: return response data as-is
    return response.data;
  }

  // --- CRUD Methods ---
  // 4. Explicitly type ALL parameters in methods (Fixes TS7006)
  async get(endpoint: string, params: Record<string, any> = {}): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.get(endpoint, { params });
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`GET request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  async getById(endpoint: string, id: string | number): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.get(`${endpoint}/${id}`);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`GET by ID request failed for ${endpoint}/${id}:`, error);
      throw error;
    }
  }

  // Corrected: Added 'data:' before the type annotation
  async post(
    endpoint: string,
    data: Record<string, any> = {},
    config: Record<string, any> = {}
  ): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.post(endpoint, data, config);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`POST request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Corrected: Added 'data:' before the type annotation
  async put(endpoint: string, id: string | number, data: Record<string, any> = {}): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.put(`${endpoint}/${id}`, data);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PUT request failed for ${endpoint}/${id}:`, error);
      throw error;
    }
  }

  // Corrected: Added 'data:' before the type annotation
  async patch(endpoint: string, id: string | number, data: Record<string, any> = {}): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`${endpoint}/${id}`, data);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for ${endpoint}/${id}:`, error);
      throw error;
    }
  }

  async delete(endpoint: string, id: string | number): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.delete(`${endpoint}/${id}`);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`DELETE request failed for ${endpoint}/${id}:`, error);
      throw error;
    }
  }

  async uploadFile(endpoint: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response: AxiosResponse = await this.apiClient.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data', // Important!
        },
      });

      // Return the full response data which should contain { url, name }
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`File upload failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // --- Blog Categories API Methods ---
  async getBlogCategories(params: Record<string, any> = {}): Promise<any> {
    return this.get('blogs/categories', params);
  }

  async getBlogCategoryById(id: string): Promise<any> {
    return this.getById('blogs/categories', id);
  }

  async createBlogCategory(data: Record<string, any>): Promise<any> {
    return this.post('blogs/categories', data);
  }

  async updateBlogCategory(id: string, data: Record<string, any>): Promise<any> {
    return this.put('blogs/categories', id, data);
  }

  async deleteBlogCategory(id: string): Promise<any> {
    return this.delete('blogs/categories', id);
  }

  async toggleBlogCategoryStatus(id: string, status: boolean): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`blogs/categories/${id}/toggle-status`, { is_active: status });
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for blogs/categories/${id}/toggle-status:`, error);
      throw error;
    }
  }

  async toggleBlogCategoryFeatured(id: string, featured: boolean): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`blogs/categories/${id}/toggle-featured`, { is_featured: featured });
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for blogs/categories/${id}/toggle-featured:`, error);
      throw error;
    }
  }

  async reorderBlogCategories(categories: Array<{ id: string; sort_order: number }>): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch('blogs/categories/reorder', { categories });
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error('PATCH request failed for blogs/categories/reorder:', error);
      throw error;
    }
  }

  // --- Blogs API Methods ---
  async getBlogs(params: Record<string, any> = {}): Promise<any> {
    return this.get('blogs', params);
  }

  async getBlogById(id: string): Promise<any> {
    return this.getById('blogs', id);
  }

  async getBlogBySlug(slug: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.get(`blogs/slug/${slug}`);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`GET request failed for blogs/slug/${slug}:`, error);
      throw error;
    }
  }

  async createBlog(data: Record<string, any>): Promise<any> {
    return this.post('blogs', data);
  }

  async updateBlog(id: string, data: Record<string, any>): Promise<any> {
    return this.put('blogs', id, data);
  }

  async deleteBlog(id: string): Promise<any> {
    return this.delete('blogs', id);
  }

  async publishBlog(id: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`blogs/${id}/publish`, {
        publish_at: new Date().toISOString()
      });
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for blogs/${id}/publish:`, error);
      throw error;
    }
  }

  async unpublishBlog(id: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`blogs/${id}/unpublish`);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for blogs/${id}/unpublish:`, error);
      throw error;
    }
  }

  async toggleBlogFeatured(id: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`blogs/${id}/toggle-featured`);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for blogs/${id}/toggle-featured:`, error);
      throw error;
    }
  }

  async toggleBlogStatus(id: string, status: boolean): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`blogs/${id}/toggle-status`, { is_active: status });
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for blogs/${id}/toggle-status:`, error);
      throw error;
    }
  }

  async incrementBlogViews(id: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`blogs/${id}/increment-views`);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for blogs/${id}/increment-views:`, error);
      throw error;
    }
  }

  async incrementBlogLikes(id: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`blogs/${id}/increment-likes`);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for blogs/${id}/increment-likes:`, error);
      throw error;
    }
  }

  async incrementBlogShares(id: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`blogs/${id}/increment-shares`);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for blogs/${id}/increment-shares:`, error);
      throw error;
    }
  }

  async getFeaturedBlogs(params: Record<string, any> = {}): Promise<any> {
    return this.get('blogs/featured', params);
  }

  async getTrendingBlogs(params: Record<string, any> = {}): Promise<any> {
    return this.get('blogs/trending', params);
  }

  async getBlogsByCategory(categoryId: string, params: Record<string, any> = {}): Promise<any> {
    return this.get(`blogs/category/${categoryId}`, params);
  }

  async getBlogsByAuthor(authorId: string, params: Record<string, any> = {}): Promise<any> {
    return this.get(`blogs/author/${authorId}`, params);
  }

  async getBlogsByTag(tag: string, params: Record<string, any> = {}): Promise<any> {
    return this.get(`blogs/tag/${tag}`, params);
  }

  async searchBlogs(query: string, params: Record<string, any> = {}): Promise<any> {
    return this.get('blogs/search', { q: query, ...params });
  }



  // --- Knowledge Categories API Methods ---
  async getKnowledgeCategories(params: Record<string, any> = {}): Promise<any> {
    return this.get('knowledge/categories', params);
  }

  async getKnowledgeCategoryById(id: string): Promise<any> {
    return this.getById('knowledge/categories', id);
  }

  async createKnowledgeCategory(data: Record<string, any>): Promise<any> {
    return this.post('knowledge/categories', data);
  }

  async updateKnowledgeCategory(id: string, data: Record<string, any>): Promise<any> {
    return this.put('knowledge/categories', id, data);
  }

  async deleteKnowledgeCategory(id: string): Promise<any> {
    return this.delete('knowledge/categories', id);
  }

  async toggleKnowledgeCategoryStatus(id: string, status: boolean): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`knowledge/categories/${id}/toggle-status`, { is_active: status });
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for knowledge/categories/${id}/toggle-status:`, error);
      throw error;
    }
  }

  async toggleKnowledgeCategoryFeatured(id: string, featured: boolean): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`knowledge/categories/${id}/toggle-featured`, { is_featured: featured });
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for knowledge/categories/${id}/toggle-featured:`, error);
      throw error;
    }
  }

  async reorderKnowledgeCategories(categories: Array<{ id: string; sort_order: number }>): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch('knowledge/categories/reorder', { categories });
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error('PATCH request failed for knowledge/categories/reorder:', error);
      throw error;
    }
  }

  // --- Blogs API Methods ---
  async getKnowledge(params: Record<string, any> = {}): Promise<any> {
    return this.get('knowledge', params);
  }

  async getKnowledgeById(id: string): Promise<any> {
    return this.getById('knowledge', id);
  }

  async getKnowledgeBySlug(slug: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.get(`knowledge/slug/${slug}`);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`GET request failed for knowledge/slug/${slug}:`, error);
      throw error;
    }
  }

  async createKnowledge(data: Record<string, any>): Promise<any> {
    return this.post('knowledge', data);
  }

  async updateKnowledge(id: string, data: Record<string, any>): Promise<any> {
    return this.put('knowledge', id, data);
  }

  async deleteKnowledge(id: string): Promise<any> {
    return this.delete('knowledge', id);
  }

  async publishKnowledge(id: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`knowledge/${id}/publish`, {
        publish_at: new Date().toISOString()
      });
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for knowledge/${id}/publish:`, error);
      throw error;
    }
  }

  async unpublishKnowledge(id: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`knowledge/${id}/unpublish`);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for knowledge/${id}/unpublish:`, error);
      throw error;
    }
  }

  async toggleKnowledgeFeatured(id: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`knowledge/${id}/toggle-featured`);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for knowledge/${id}/toggle-featured:`, error);
      throw error;
    }
  }

  async toggleKnowledgeStatus(id: string, status: boolean): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`knowledge/${id}/toggle-status`, { is_active: status });
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for knowledge/${id}/toggle-status:`, error);
      throw error;
    }
  }

  async incrementKnowledgeViews(id: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`knowledge/${id}/increment-views`);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for knowledge/${id}/increment-views:`, error);
      throw error;
    }
  }

  async incrementKnowledgeLikes(id: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`knowledge/${id}/increment-likes`);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for knowledge/${id}/increment-likes:`, error);
      throw error;
    }
  }

  async incrementKnowledgeShares(id: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`knowledge/${id}/increment-shares`);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for Knowledge/${id}/increment-shares:`, error);
      throw error;
    }
  }

  async getFeaturedKnowledge(params: Record<string, any> = {}): Promise<any> {
    return this.get('knowledge/featured', params);
  }

  async getTrendingKnowledge(params: Record<string, any> = {}): Promise<any> {
    return this.get('knowledge/trending', params);
  }

  async getKnowledgeByCategory(categoryId: string, params: Record<string, any> = {}): Promise<any> {
    return this.get(`knowledge/category/${categoryId}`, params);
  }

  async getKnowledgeByAuthor(authorId: string, params: Record<string, any> = {}): Promise<any> {
    return this.get(`knowledge/author/${authorId}`, params);
  }

  async getKnowledgeByTag(tag: string, params: Record<string, any> = {}): Promise<any> {
    return this.get(`knowledge/tag/${tag}`, params);
  }

  async searchKnowledgeBlogs(query: string, params: Record<string, any> = {}): Promise<any> {
    return this.get('knowledge/search', { q: query, ...params });
  }

  // --- Release Notes API Methods ---
  async getReleaseNotes(params: Record<string, any> = {}): Promise<any> {
    return this.get('common/releasenotes', params);
  }

  async getReleaseNoteById(id: string): Promise<any> {
    return this.getById('common/releasenotes', id);
  }

  async createReleaseNote(data: Record<string, any>): Promise<any> {
    return this.post('common/releasenotes', data);
  }

  async updateReleaseNote(id: string, data: Record<string, any>): Promise<any> {
    return this.put('common/releasenotes', id, data);
  }

  async deleteReleaseNote(id: string): Promise<any> {
    return this.delete('common/releasenotes', id);
  }

  // --- User/Profile API Methods ---
  async getCurrentUser(): Promise<any> {
    return this.get('auth/me');
  }

  // Helper method to check authentication status
  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token;
  }

  // Debug method to check token status
  getTokenStatus(): { hasToken: boolean; tokenLength: number; tokenPreview: string } {
    const token = this.getToken();
    return {
      hasToken: !!token,
      tokenLength: token?.length || 0,
      tokenPreview: token?.substring(0, 10) || 'none'
    };
  }

  // --- Package Categories API Methods ---

  // Get all package categories with optional filters
  async getPackageCategories(params: Record<string, any> = {}): Promise<any> {
    return this.get('billing/admin/categories', params);
  }

  // Get package category by ID
  async getPackageCategoryById(id: string): Promise<any> {
    return this.get(`billing/admin/categories/${id}`);
  }

  // Create new package category
  async createPackageCategory(data: {
    key: string;
    name: string;
    description?: string;
    icon?: string;
    color?: string;
    sortOrder?: number;
  }): Promise<any> {
    return this.post('billing/admin/categories', data);
  }

  // Update package category
  async updatePackageCategory(id: string, data: {
    key?: string;
    name?: string;
    description?: string;
    icon?: string;
    color?: string;
    sortOrder?: number;
  }): Promise<any> {
    return this.put(`billing/admin/categories`, id, data);
  }

  // Delete package category
  async deletePackageCategory(id: string, force: boolean = false): Promise<any> {
    const endpoint = `billing/admin/categories/${id}${force ? '?force=true' : ''}`;
    try {
      const response: AxiosResponse = await this.apiClient.delete(endpoint);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`DELETE request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Toggle package category status
  async togglePackageCategoryStatus(id: string): Promise<any> {
    return this.post(`billing/admin/categories/${id}/toggle`);
  }

  // --- Currency Management API Methods ---

  // Get all currencies with optional filters
  async getCurrencies(params: Record<string, any> = {}): Promise<any> {
    return this.get('billing/admin/currencies', params);
  }

  // Get currency by ID
  async getCurrencyById(id: string): Promise<any> {
    return this.get(`billing/admin/currencies/${id}`);
  }

  // Create new currency
  async createCurrency(data: {
    code: string;
    name: string;
    symbol: string;
    symbolPosition?: 'before' | 'after';
    decimalPlaces?: number;
    sortOrder?: number;
    isDefault?: boolean;
  }): Promise<any> {
    return this.post('billing/admin/currencies', data);
  }

  // Update currency
  async updateCurrency(id: string, data: {
    name?: string;
    symbol?: string;
    symbolPosition?: 'before' | 'after';
    decimalPlaces?: number;
    sortOrder?: number;
    isDefault?: boolean;
  }): Promise<any> {
    return this.put('billing/admin/currencies', id, data);
  }

  // Delete currency
  async deleteCurrency(id: string, force: boolean = false): Promise<any> {
    const queryParams = force ? '?force=true' : '?force=false';
    const endpoint = `billing/admin/currencies/${id}${queryParams}`;
    try {
      const response: AxiosResponse = await this.apiClient.delete(endpoint);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`DELETE request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Toggle currency status
  async toggleCurrencyStatus(id: string): Promise<any> {
    return this.post(`billing/admin/currencies/${id}/toggle`);
  }

  // --- Feature Management API Methods ---

  // Get all features with optional filters
  async getFeatures(params: {
    page?: number;
    limit?: number;
    category?: string;
    isActive?: string | boolean;
    search?: string;
    sortBy?: string;
  } = {}): Promise<any> {

    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.category) queryParams.append('category', params.category);
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);

    const endpoint = `billing/admin/features?${queryParams.toString()}`;
    console.log("🔍 Final endpoint:", endpoint);

    try {
      console.log("🔍 Making direct axios call to:", endpoint);
      const response = await this.apiClient.get(endpoint);
      console.log("🔍 Direct axios response:", response);
      console.log("🔍 Response data:", response.data);

      const standardized = this.standardizeResponse(response);
      console.log("🔍 Standardized response:", standardized);
      return standardized;
    } catch (error) {
      console.error("🔍 Error in getFeatures:", error);
      throw error;
    }
  }

  // Get feature by ID
  async getFeatureById(id: string): Promise<any> {
    const response = await this.get(`billing/admin/features/${id}`);
    return this.standardizeResponse(response);
  }

  // Create new feature
  async createFeature(data: {
    key: string;
    name: string;
    description?: string;
    category: string;
    enabledByDefault?: boolean;
    limits?: Record<string, any>;
    metadata?: Record<string, any>;
  }): Promise<any> {
    const response = await this.post('billing/admin/features', data);
    return this.standardizeResponse(response);
  }

  // Update feature
  async updateFeature(id: string, data: {
    key?: string;
    name?: string;
    description?: string;
    category?: string;
    enabledByDefault?: boolean;
    limits?: Record<string, any>;
    metadata?: Record<string, any>;
  }): Promise<any> {
    const response = await this.put('billing/admin/features', id, data);
    return this.standardizeResponse(response);
  }

  // Delete feature
  async deleteFeature(id: string, force: boolean = false): Promise<any> {
    const queryParams = force ? '?force=true' : '?force=false';
    const endpoint = `billing/admin/features/${id}${queryParams}`;
    try {
      const response: AxiosResponse = await this.apiClient.delete(endpoint);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`DELETE request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  // Toggle feature status
  async toggleFeatureStatus(id: string): Promise<any> {
    const response = await this.post(`billing/admin/features/${id}/toggle`);
    return this.standardizeResponse(response);
  }

  // Get features by category
  async getFeaturesByCategory(categoryId: string, params: {
    isActive?: boolean;
  } = {}): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());

    const endpoint = `billing/admin/features/by-category/${categoryId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await this.get(endpoint);
    return this.standardizeResponse(response);
  }

  // ================================
  // PACKAGE MANAGEMENT METHODS
  // ================================

  // Get packages with filtering and pagination
  async getPackages(params: {
    page?: number;
    limit?: number;
    packageType?: string;
    isActive?: boolean;
    isPublic?: boolean;
    search?: string;
  } = {}): Promise<any> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.packageType) queryParams.append('packageType', params.packageType);
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params.isPublic !== undefined) queryParams.append('isPublic', params.isPublic.toString());
    if (params.search) queryParams.append('search', params.search);

    const endpoint = `billing/admin/packages${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await this.get(endpoint);
    return response;
  }

  // Create a new package
  async createPackage(packageData: {
    slug: string;
    name: string;
    description?: string;
    packageType: string;
    category?: string;
    isPublic?: boolean;
    sortOrder?: number;
    features?: Array<{
      featureId: string;
      enabled: boolean;
      limits?: Record<string, any>;
    }>;
    prices?: Array<{
      currency: string;
      amount: number;
      interval: string;
      priceModel: string;
      setupFee?: number;
      trialDays?: number;
    }>;
    allowDowngrade?: boolean;
    allowUpgrade?: boolean;
    gracePeriodDays?: number;
    metadata?: Record<string, any>;
  }): Promise<any> {
    const response = await this.post('billing/admin/packages', packageData);
    return this.standardizeResponse(response);
  }

  // Update an existing package
  async updatePackage(packageId: string, packageData: {
    slug?: string;
    name?: string;
    description?: string;
    packageType?: string;
    category?: string;
    isPublic?: boolean;
    sortOrder?: number;
    features?: Array<{
      featureId: string;
      enabled: boolean;
      limits?: Record<string, any>;
    }>;
    prices?: Array<{
      currency: string;
      amount: number;
      interval: string;
      priceModel: string;
      setupFee?: number;
      trialDays?: number;
    }>;
    allowDowngrade?: boolean;
    allowUpgrade?: boolean;
    gracePeriodDays?: number;
    metadata?: Record<string, any>;
  }): Promise<any> {
    const response = await this.put('billing/admin/packages', packageId, packageData);
    return this.standardizeResponse(response);
  }

  // Duplicate a package
  async duplicatePackage(packageId: string, duplicateData: {
    slug: string;
    name: string;
  }): Promise<any> {
    const response = await this.post(`billing/admin/packages/${packageId}/duplicate`, duplicateData);
    return this.standardizeResponse(response);
  }

  // Delete a package
  async deletePackage(packageId: string): Promise<any> {
    const response = await this.delete('billing/admin/packages', packageId);
    return this.standardizeResponse(response);
  }

  // Toggle package status (activate/deactivate)
  async togglePackageStatus(packageId: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`billing/admin/packages/${packageId}/toggle-status`);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for billing/admin/packages/${packageId}/toggle-status:`, error);
      throw error;
    }
  }

  // Get packages by type
  async getPackagesByType(packageType: string, params: {
    isActive?: boolean;
    isPublic?: boolean;
  } = {}): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params.isPublic !== undefined) queryParams.append('isPublic', params.isPublic.toString());

    const endpoint = `billing/admin/packages/by-type/${packageType}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await this.get(endpoint);
    return this.standardizeResponse(response);
  }

  // =====================================
  // SUBSCRIPTION MANAGEMENT METHODS
  // =====================================

  // Get all subscriptions with filtering and pagination
  async getSubscriptions(params: {
    page?: number;
    limit?: number;
    status?: string;
    userId?: string;
    packageId?: string;
    workspaceId?: string;
    search?: string;
    type?: string;
  } = {}): Promise<any> {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.userId) queryParams.append('userId', params.userId);
    if (params.packageId) queryParams.append('packageId', params.packageId);
    if (params.workspaceId) queryParams.append('workspaceId', params.workspaceId);
    if (params.search) queryParams.append('search', params.search);
    if (params.type) queryParams.append('type', params.type);

    const endpoint = `billing/admin/subscriptions${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await this.get(endpoint);
    return response;
  }

  // Get subscription by ID
  async getSubscriptionById(id: string): Promise<any> {
    const endpoint = `billing/admin/subscriptions/${id}`;
    const response = await this.get(endpoint);
    return response;
  }

  // Create admin subscription
  async createSubscription(subscriptionData: {
    userId: string;
    packageId: string;
    workspaceId?: string;
    interval: string;
    status: string;
    customPrice?: {
      currency: string;
      amount: number;
      interval: string;
      priceModel: string;
    };
    trialDays?: number;
    notes?: string;
  }): Promise<any> {
    const endpoint = 'billing/admin/subscriptions';
    const response = await this.post(endpoint, subscriptionData);
    return response;
  }

  // Update subscription
  async updateSubscription(id: string, subscriptionData: {
    status?: string;
    packageId?: string;
    customPrice?: {
      currency?: string;
      amount?: number;
      interval?: string;
      priceModel?: string;
    };
    notes?: string;
    workspaceId?: string;
    interval?: string;
    trialDays?: number;
  }): Promise<any> {
    const endpoint = 'billing/admin/subscriptions';
    const response = await this.put(endpoint, id, subscriptionData);
    return response;
  }

  // Cancel subscription
  async cancelSubscription(id: string): Promise<any> {
    const endpoint = `billing/admin/subscriptions/${id}/cancel`;
    const response = await this.post(endpoint, {});
    return response;
  }

  // Pause subscription
  async pauseSubscription(id: string, pauseData?: { resumeAt?: string }): Promise<any> {
    const endpoint = `billing/admin/subscriptions/${id}/pause`;
    const response = await this.post(endpoint, pauseData || {});
    return response;
  }

  // Resume subscription
  async resumeSubscription(id: string): Promise<any> {
    const endpoint = `billing/admin/subscriptions/${id}/resume`;
    const response = await this.post(endpoint, {});
    return response;
  }

  // --- Knowledge Bases API Methods ---
  // Get all knowledge bases
  async getKnowledgeBases(params: Record<string, any> = {}): Promise<any> {
    return this.get('admin/knowledge-bases', params);
  }

  // Get knowledge base by ID
  async getKnowledgeBaseById(id: string): Promise<any> {
    return this.getById('admin/knowledge-bases', id);
  }

  // Create knowledge base
  async createKnowledgeBase(data: Record<string, any>): Promise<any> {
    return this.post('admin/knowledge-bases', data);
  }

  // Update knowledge base
  async updateKnowledgeBase(id: string, data: Record<string, any>): Promise<any> {
    return this.patch('admin/knowledge-bases', id, data);
  }

  // Delete knowledge base
  async deleteKnowledgeBase(id: string): Promise<any> {
    return this.delete('admin/knowledge-bases', id);
  }

  // Toggle knowledge base status
  async toggleKnowledgeBaseStatus(id: string, isActive: boolean): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`admin/knowledge-bases/${id}/toggle-status`, { is_active: isActive });
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`Toggle status failed for knowledge base ${id}:`, error);
      throw error;
    }
  }

  // Get documents for a knowledge base
  async getKnowledgeBaseDocuments(knowledgeBaseId: string, params: Record<string, any> = {}): Promise<any> {
    return this.get(`admin/knowledge-bases/${knowledgeBaseId}/documents`, params);
  }

  // Upload document to knowledge base (file)
  async uploadKnowledgeBaseDocument(knowledgeBaseId: string, file: File, metadata?: Record<string, any>): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);

    if (metadata) {
      Object.keys(metadata).forEach(key => {
        formData.append(key, metadata[key]);
      });
    }

    try {
      const response: AxiosResponse = await this.apiClient.post(
        `admin/knowledge-bases/${knowledgeBaseId}/documents/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`Document upload failed for KB ${knowledgeBaseId}:`, error);
      throw error;
    }
  }

  // Add URL document to knowledge base
  async addKnowledgeBaseUrl(knowledgeBaseId: string, url: string, title?: string): Promise<any> {
    return this.post(`admin/knowledge-bases/${knowledgeBaseId}/documents/url`, { url, title });
  }

  // Add text document to knowledge base
  async addKnowledgeBaseText(knowledgeBaseId: string, title: string, text: string): Promise<any> {
    return this.post(`admin/knowledge-bases/${knowledgeBaseId}/documents/text`, { title, text });
  }

  // Generate knowledge base from prompt
  async generateKnowledgeBasePrompt(knowledgeBaseId: string, payload: { title: string; prompt: string }): Promise<any> {
    return this.post(`admin/knowledge-bases/${knowledgeBaseId}/generate`, payload);
  }

  // Get document details
  async getKnowledgeBaseDocument(knowledgeBaseId: string, documentId: string): Promise<any> {
    return this.get(`admin/knowledge-bases/${knowledgeBaseId}/documents/${documentId}`);
  }

  // Delete document
  async deleteKnowledgeBaseDocument(knowledgeBaseId: string, documentId: string): Promise<any> {
    return this.delete(`admin/knowledge-bases/${knowledgeBaseId}/documents`, documentId);
  }

  // Test retrieval from knowledge base
  async testKnowledgeBaseRetrieval(knowledgeBaseId: string, query: string, topK: number = 5): Promise<any> {
    return this.post(`admin/knowledge-bases/${knowledgeBaseId}/retrieval/test`, { query, topK });
  }

  // --- Agents API Methods ---

  // Get all agents
  async getAgents(params: Record<string, any> = {}): Promise<any> {
    return this.get('admin/agents', params);
  }

  // Get agent by ID
  async getAgentById(id: string): Promise<any> {
    return this.getById('admin/agents', id);
  }

  // Create agent
  async createAgent(data: Record<string, any>): Promise<any> {
    return this.post('admin/agents', data);
  }

  // Update agent
  async updateAgent(id: string, data: Record<string, any>): Promise<any> {
    return this.put('admin/agents', id, data);
  }

  // Delete agent
  async deleteAgent(id: string): Promise<any> {
    return this.delete('admin/agents', id);
  }

  // Toggle agent active status
  async toggleAgentStatus(id: string, isActive: boolean): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`admin/agents/${id}/toggle-status`, { isActive });
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for agents/${id}/toggle-status:`, error);
      throw error;
    }
  }

  // Get agent assignments
  async getAgentAssignments(agentId: string, params: Record<string, any> = {}): Promise<any> {
    return this.get(`admin/agents/${agentId}/assignments`, params);
  }

  // Create agent assignment
  async createAgentAssignment(agentId: string, data: Record<string, any>): Promise<any> {
    return this.post(`admin/agents/${agentId}/assignments`, data);
  }

  // Delete agent assignment
  async deleteAgentAssignment(agentId: string, assignmentId: string): Promise<any> {
    return this.delete(`admin/agents/${agentId}/assignments`, assignmentId);
  }

  // Test agent chat (preview)
  async testAgentChat(agentId: string, message: string, conversationHistory?: any[]): Promise<any> {
    return this.post(`admin/agents/${agentId}/test-chat`, { message, conversationHistory });
  }

  // Search users (for user picker in assignments)
  async searchUsers(query: string, params: Record<string, any> = {}): Promise<any> {
    return this.get('users/search', { ...params, query });
  }

  // --- Agent Meta API Methods ---
  async getAgentCapabilities(): Promise<any> {
    return this.get('cms-agent-meta/capabilities');
  }

  async getAgentRoles(): Promise<any> {
    return this.get('cms-agent-meta/roles');
  }

  async getAgentCompetencies(): Promise<any> {
    return this.get('cms-agent-meta/competencies');
  }

  async getAgentRules(): Promise<any> {
    return this.get('cms-agent-meta/rules');
  }

  async getAgentSkills(): Promise<any> {
    return this.get('cms-agent-meta/skills');
  }

  async getAgentLevels(): Promise<any> {
    return this.get('cms-agent-meta/levels');
  }

  // --- CMS OTP & Login Methods ---
  async sendLoginOTP(email: string): Promise<any> {
    return this.post('cms/send-otp', { email });
  }

  async verifyLoginOTP(email: string, otp: string): Promise<any> {
    return this.post('cms/verify-otp', { email, otp });
  }

  // --- CMS Users API Methods ---
  async toggleCMSUserInternal(id: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`cms-users/${id}/toggle-internal`);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for cms-users/${id}/toggle-internal:`, error);
      throw error;
    }
  }

  async toggleUserInternal(id: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`users/${id}/toggle-internal`);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for users/${id}/toggle-internal:`, error);
      throw error;
    }
  }

  // --- Company Management API Methods ---
  async getCompanies(params: Record<string, any> = {}): Promise<any> {
    return this.get('cms-companies', params);
  }

  async getCompanyById(id: string): Promise<any> {
    return this.getById('cms-companies', id);
  }

  async updateCompany(id: string, data: Record<string, any>): Promise<any> {
    return this.patch('cms-companies', id, data);
  }

  async deleteCompany(id: string): Promise<any> {
    return this.delete('cms-companies', id);
  }

  async toggleCompanyStatus(id: string): Promise<any> {
    try {
      const response: AxiosResponse = await this.apiClient.patch(`cms-companies/${id}/status`);
      return this.standardizeResponse(response);
    } catch (error: any) {
      console.error(`PATCH request failed for cms-companies/${id}/status:`, error);
      throw error;
    }
  }

  async getCompanyMembers(id: string, params: Record<string, any> = {}): Promise<any> {
    return this.get(`cms-companies/${id}/members`, params);
  }

  async getCompanyWorkspaces(id: string, params: Record<string, any> = {}): Promise<any> {
    return this.get(`cms-companies/${id}/workspaces`, params);
  }

  // --- Dashboard API Methods ---
  async getDashboardCounts(): Promise<any> {
    return this.get('cms-dashboard/counts');
  }

  async getTokenUsageTotals(params: Record<string, any> = {}): Promise<any> {
    return this.get('cms-dashboard/token-usage-totals', params);
  }

} // <-- Make sure the class block ends here



// ================================
// EXPORTS - MUST BE OUTSIDE THE CLASS
// ================================
// Create and export a singleton instance
const apiService = new APICaller();
export default apiService;

// Export the class itself if needed
export { APICaller };